import { useEffect, useRef } from 'react';
import {
  Quaternion,
  type QuaternionTuple,
  Vector3,
  type Vector3Tuple,
} from 'three';

import type { TOOL_CONST } from '@/constant';
import { useShortcut } from '@/package';
import { useLabelingBoxContext } from '@/provider';
import {
  useEditingLabelBoxStore,
  useToolMenuStore,
  useToolPermissionStore,
  useToolSettingStore,
} from '@/store';
import { getVectorByAxis, toRadian } from '@/util';

import BoxFocusController from './BoxFocusController';
import { FSTEditingLabelBox } from './EditingLabelBox';
import ToolGrid from './ToolGrid';
import type { ToolPcdCanvasProps } from './ToolPcdCanvas';
import { ToolPcdCanvasContextProvider } from './ToolPcdCanvas';
import ToolPcdSurfaceViewCamera from './ToolPcdSurfaceViewCamera';

const ToolPcdCanvasFSTViewMode = ({
  viewMode,
  ...props
}: ToolPcdCanvasProps & {
  viewMode: Exclude<TOOL_CONST.TOOL_CAMERA_VIEW_MODE, 'default'>;
}) => {
  const { namespace, register, unregister } = useShortcut();
  const { toolMap, findByName, select } = useToolMenuStore();
  const {
    selectedLabelingBoxList: selectedBoxList,
    moveBoxList,
    resizeBoxList,
    updateQuaternion,
  } = useLabelingBoxContext();
  const { stepSizeConfig } = useToolSettingStore();
  const { updateTransformedBoxData } = useEditingLabelBoxStore();
  const { permission } = useToolPermissionStore();
  const selectedBoxListRef = useRef(selectedBoxList);
  const firstSelectedBox = selectedBoxList?.[0];
  const moveZTool = findByName('labelBoxMove+Z')!;
  const move_ZTool = findByName('labelBoxMove-Z')!;
  const moveXTool = findByName('labelBoxMove+X')!;
  const move_XTool = findByName('labelBoxMove-X')!;
  const moveYTool = findByName('labelBoxMove+Y')!;
  const move_YTool = findByName('labelBoxMove-Y')!;
  const rotate90Tool = findByName('labelBoxRotate90')!;
  const rotate180Tool = findByName('labelBoxRotate180')!;
  const rotateClockwiseTool = findByName('labelBoxRotateClockwise')!;
  const rotateCounterClockwiseTool = findByName(
    'labelBoxRotateCounterClockwise',
  )!;
  const decreaseHeightTool = findByName('labelBoxDecreaseHeight')!;
  const increaseHeightTool = findByName('labelBoxIncreaseHeight')!;
  const decreaseDepthTool = findByName('labelBoxDecreaseDepth')!;
  const increaseDepthTool = findByName('labelBoxIncreaseDepth')!;

  useEffect(() => {
    selectedBoxListRef.current = selectedBoxList;
  }, [selectedBoxList]);

  useEffect(() => {
    if (!selectedBoxListRef.current.length) return;

    const moveOffset = [0, 0, 0];
    const moveStepSize = stepSizeConfig.move;

    if (moveZTool.isSelected) moveOffset[2] += moveStepSize;
    else if (move_ZTool.isSelected) moveOffset[2] -= moveStepSize;
    else if (moveXTool.isSelected) moveOffset[0] += moveStepSize;
    else if (move_XTool.isSelected) moveOffset[0] -= moveStepSize;
    else if (moveYTool.isSelected) moveOffset[1] += moveStepSize;
    else if (move_YTool.isSelected) moveOffset[1] -= moveStepSize;

    if (!moveOffset.reduce((acc, cur) => acc + cur)) return;

    const changeList = selectedBoxListRef.current.map(box => {
      // const position = box.position.value;
      const position = box.position;

      const moveOffsetVector = new Vector3(...moveOffset).applyQuaternion(
        new Quaternion(...box.quaternion),
      );

      return [
        box.id,
        [
          position[0] + moveOffsetVector.x,
          position[1] + moveOffsetVector.y,
          position[2] + moveOffsetVector.z,
        ],
      ] as [string, Vector3Tuple];
    });

    moveBoxList(changeList);

    updateTransformedBoxData({ position: changeList[0][1] });
  }, [toolMap, stepSizeConfig.move]);

  useEffect(() => {
    let degree = 0;

    const rotateStepSize = stepSizeConfig.rotate;

    if (rotateClockwiseTool.isSelected) degree = rotateStepSize;
    else if (rotateCounterClockwiseTool.isSelected) degree = -rotateStepSize;
    else if (rotate90Tool.isSelected) degree = 90;
    else if (rotate180Tool.isSelected) degree = 180;

    if (degree === 0) return;

    const changeList = selectedBoxListRef.current.map(box => {
      const rotationQuat = new Quaternion().setFromAxisAngle(
        getVectorByAxis('z'),
        -toRadian(degree),
      );

      const newQuat = rotationQuat
        .multiply(box.boxMesh.quaternion.clone())
        .toArray();

      return [box.id, newQuat] as [string, QuaternionTuple];
    });

    updateQuaternion(changeList);
  }, [toolMap, stepSizeConfig.rotate]);

  useEffect(() => {
    if (!selectedBoxListRef.current.length) return;

    let heightOffset = 0;

    if (increaseHeightTool.isSelected) heightOffset = 0.1;
    else if (decreaseHeightTool.isSelected) heightOffset = -0.1;

    if (heightOffset === 0) return;

    const changeList = selectedBoxListRef.current.map(box => {
      const dimension = box.dimension;
      const position = box.position;
      const newDimension: Vector3Tuple = [
        dimension[0],
        dimension[1],
        dimension[2] + heightOffset,
      ];

      const positionOffset = heightOffset / 2;
      const newPosition: Vector3Tuple = [
        position[0],
        position[1],
        position[2] + positionOffset,
      ];

      return [box.id, newDimension, newPosition] as [
        string,
        Vector3Tuple,
        Vector3Tuple,
      ];
    });

    resizeBoxList(changeList.map(([id, dim]) => [id, dim]));
    moveBoxList(changeList.map(([id, _, pos]) => [id, pos]));
    updateTransformedBoxData({
      dimension: changeList[0][1],
      position: changeList[0][2],
    });
  }, [toolMap]);

  useEffect(() => {
    if (!selectedBoxListRef.current.length) return;

    let depthOffset = 0;

    if (increaseDepthTool.isSelected) depthOffset = 0.1;
    else if (decreaseDepthTool.isSelected) depthOffset = -0.1;

    if (depthOffset === 0) return;

    const changeList = selectedBoxListRef.current.map(box => {
      const dimension = box.dimension;
      const newDimension: Vector3Tuple = [
        dimension[0],
        dimension[1] + depthOffset,
        dimension[2],
      ];

      return [box.id, newDimension] as [string, Vector3Tuple];
    });

    resizeBoxList(changeList);
    updateTransformedBoxData({ dimension: changeList[0][1] });
  }, [toolMap, viewMode]);

  useEffect(() => {
    register(moveZTool.command!, () => select(moveZTool.id));
    register(move_ZTool.command!, () => select(move_ZTool.id));
    register(moveXTool.command!, () => select(moveXTool.id));
    register(move_XTool.command!, () => select(move_XTool.id));
    register(moveYTool.command!, () => select(moveYTool.id));
    register(move_YTool.command!, () => select(move_YTool.id));
    register(rotate90Tool.command!, () => select(rotate90Tool.id));
    register(rotate180Tool.command!, () => select(rotate180Tool.id));
    register(rotateClockwiseTool.command!, () =>
      select(rotateClockwiseTool.id),
    );
    register(rotateCounterClockwiseTool.command!, () =>
      select(rotateCounterClockwiseTool.id),
    );
    register(decreaseHeightTool.command!, () => select(decreaseHeightTool.id));
    register(increaseHeightTool.command!, () => select(increaseHeightTool.id));
    register(decreaseDepthTool.command!, () => select(decreaseDepthTool.id));
    register(increaseDepthTool.command!, () => select(increaseDepthTool.id));

    return () => {
      unregister(moveZTool.command!);
      unregister(move_ZTool.command!);
      unregister(moveXTool.command!);
      unregister(move_XTool.command!);
      unregister(moveYTool.command!);
      unregister(move_YTool.command!);
      unregister(rotate90Tool.command!);
      unregister(rotate180Tool.command!);
      unregister(rotateClockwiseTool.command!);
      unregister(rotateCounterClockwiseTool.command!);
      unregister(decreaseHeightTool.command!);
      unregister(increaseHeightTool.command!);
      unregister(decreaseDepthTool.command!);
      unregister(increaseDepthTool.command!);
    };
  }, [namespace]);

  return (
    <ToolPcdCanvasContextProvider
      {...props}
      enabledDragBox={false}
      viewMode={viewMode}
    >
      {firstSelectedBox && Object.hasOwn(firstSelectedBox, 'id') && (
        <FSTEditingLabelBox
          key={firstSelectedBox.id}
          box={firstSelectedBox}
          enabled={permission.labeling.updatable}
        />
      )}

      <ToolPcdSurfaceViewCamera />
      <BoxFocusController />

      <ToolGrid />
    </ToolPcdCanvasContextProvider>
  );
};

export default ToolPcdCanvasFSTViewMode;
